<template>
  <Modal v-model="innerVisible" title="月度签到" width="860" :mask-closable="true" class="sign-in-modal">
    <div class="monthly-sign-panel">
      <div class="monthly-sign-head">
        <div class="month-switcher">
          <Button shape="circle" icon="ios-arrow-back" size="small" @click="changeMonth(-1)"></Button>
          <strong>{{ monthTitle }}</strong>
          <Button shape="circle" icon="ios-arrow-forward" size="small" :disabled="isCurrentMonth" @click="changeMonth(1)"></Button>
          <Button v-if="!isCurrentMonth" size="small" @click="goCurrentMonth">本月</Button>
        </div>
        <Tag :color="isSignedToday ? 'success' : 'warning'">{{ isSignedToday ? '今日已签到' : '今日未签到' }}</Tag>
      </div>

      <div class="monthly-sign-summary">
        <div class="sign-summary-item"><span>已签到</span><strong>{{ calendar.signedCount }}</strong><small>天</small></div>
        <div class="sign-summary-item"><span>未签到</span><strong>{{ calendar.unsignedCount }}</strong><small>天</small></div>
        <div class="sign-summary-item is-missed"><span>已错过</span><strong>{{ calendar.missedCount }}</strong><small>天</small></div>
        <div class="today-reward" v-if="todayReward">
          <span>今日</span>
          <b>{{ todayReward.currencyName }} {{ formatAmount(todayReward.currencyAmount) }}</b>
          <b v-if="todayReward.xuanjingAmount">玄晶 {{ todayReward.xuanjingAmount }}</b>
        </div>
      </div>

      <div class="monthly-calendar" :class="{ 'is-loading': loading }">
        <div v-for="weekday in weekdays" :key="weekday" class="calendar-weekday">{{ weekday }}</div>
        <div v-for="index in leadingBlankCount" :key="`blank-${index}`" class="calendar-day is-empty"></div>
        <div v-for="day in calendar.days" :key="day.date" :class="['calendar-day', `is-${day.state}`]">
          <div class="calendar-day-head"><span>{{ day.day }}</span><i>{{ stateLabel(day.state) }}</i></div>
          <div class="calendar-reward is-currency" :class="`currency-${day.currencyType}`">
            <span>{{ day.currencyType === 'coin' ? '灵' : '金' }}</span>
            <b>{{ day.currencyName }} {{ formatAmount(day.currencyAmount) }}</b>
          </div>
          <div v-if="day.xuanjingAmount" class="calendar-reward is-xuanjing">
            <span>玄</span><b>玄晶 {{ day.xuanjingAmount }}</b>
          </div>
          <Icon v-if="day.state === 'signed'" type="ios-checkmark-circle" class="signed-mark" />
        </div>
        <Spin v-if="loading" fix></Spin>
      </div>
      <Alert v-if="loadError" type="error" show-icon>{{ loadError }}</Alert>
    </div>

    <div slot="footer" class="monthly-sign-footer">
      <Button @click="innerVisible = false">关闭</Button>
      <Button type="primary" :loading="signing" :disabled="loading || !calendar.canSignToday" @click="handleSignIn">
        {{ calendar.isSignedToday ? '今日已领取' : '立即签到' }}
      </Button>
    </div>
  </Modal>
</template>

<script>
export default {
  name: "SignInModal",
  props: { value: { type: Boolean, default: false } },
  data() {
    return {
      innerVisible: this.value,
      loading: false,
      signing: false,
      loadError: "",
      displayedMonth: this.currentMonthKey(),
      weekdays: ["一", "二", "三", "四", "五", "六", "日"],
      calendar: this.emptyCalendar()
    };
  },
  computed: {
    monthTitle() {
      const parts = this.displayedMonth.split("-");
      return `${parts[0]}年${Number(parts[1])}月`;
    },
    isCurrentMonth() { return this.displayedMonth === this.currentMonthKey(); },
    leadingBlankCount() { return Math.max(0, Number(this.calendar.firstWeekday || 1) - 1); },
    isSignedToday() { return Boolean(this.calendar.isSignedToday); },
    todayReward() {
      if (!this.calendar.isCurrentMonth || !Array.isArray(this.calendar.days)) return null;
      return this.calendar.days.find(day => day.state === "today" || (day.state === "signed" && day.date === this.calendar.today)) || null;
    }
  },
  watch: {
    value(val) {
      this.innerVisible = val;
      if (val) {
        this.displayedMonth = this.currentMonthKey();
        this.loadCalendar();
      }
    },
    innerVisible(val) { this.$emit("input", val); }
  },
  methods: {
    emptyCalendar() {
      return { firstWeekday: 1, signedCount: 0, unsignedCount: 0, missedCount: 0, isCurrentMonth: true, isSignedToday: false, canSignToday: false, today: "", days: [] };
    },
    getCharaId() { return typeof this.getCookie === "function" ? this.getCookie("charaId") : ""; },
    currentMonthKey() {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    },
    loadCalendar() {
      const charaId = this.getCharaId();
      if (!charaId) { this.loadError = "角色信息失效，请重新登录"; return; }
      this.loading = true;
      this.loadError = "";
      this.$http.get(`/gamepassport/monthlySignStatus?charaId=${charaId}&month=${this.displayedMonth}`)
        .then(res => {
          const payload = res.data || {};
          if (payload.status !== 200) {
            this.loadError = payload.msg || "签到日历读取失败";
            this.calendar = this.emptyCalendar();
            return;
          }
          this.calendar = { ...this.emptyCalendar(), ...(payload.data || {}) };
        })
        .catch(() => { this.loadError = "签到日历读取失败"; this.calendar = this.emptyCalendar(); })
        .finally(() => { this.loading = false; });
    },
    changeMonth(offset) {
      const parts = this.displayedMonth.split("-").map(Number);
      const date = new Date(parts[0], parts[1] - 1 + offset, 1);
      const target = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      if (target > this.currentMonthKey()) return;
      this.displayedMonth = target;
      this.loadCalendar();
    },
    goCurrentMonth() { this.displayedMonth = this.currentMonthKey(); this.loadCalendar(); },
    stateLabel(state) { return ({ signed: "已签到", today: "今日", missed: "未签到", upcoming: "待签到" })[state] || ""; },
    formatAmount(value) {
      const amount = Number(value || 0);
      if (amount >= 10000 && amount % 10000 === 0) return `${amount / 10000}万`;
      return amount.toLocaleString("zh-CN");
    },
    handleSignIn() {
      if (this.signing || !this.calendar.canSignToday) return;
      const charaId = this.getCharaId();
      if (!charaId) { this.$Message.warning("角色信息失效，请重新登录"); return; }
      this.signing = true;
      this.$http.post(`/gamepassport/signInMonthly?charaId=${charaId}`)
        .then(res => {
          const payload = res.data || {};
          if (payload.status !== 200) {
            this.$Message.warning(payload.msg || "签到失败");
            if ((payload.msg || "").indexOf("已签到") !== -1) this.loadCalendar();
            return;
          }
          const reward = payload.data || {};
          this.$Message.success(reward.message || "签到成功");
          this.$emit("sign-success", reward);
          this.loadCalendar();
        })
        .catch(() => { this.$Notice.warning({ title: "提示", desc: "签到失败，请稍后重试" }); })
        .finally(() => { this.signing = false; });
    }
  },
  mounted() { if (this.innerVisible) this.loadCalendar(); }
};
</script>

<style scoped>
.monthly-sign-panel { color: #273444; }
.monthly-sign-head { min-height: 42px; display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.month-switcher { display: flex; align-items: center; gap: 9px; }
.month-switcher strong { min-width: 110px; color: #263545; font-size: 18px; text-align: center; }
.monthly-sign-summary { display: grid; grid-template-columns: 100px 100px 100px minmax(220px, 1fr); min-height: 62px; margin-bottom: 12px; overflow: hidden; background: #f7f9fb; border: 1px solid #e1e6eb; border-radius: 6px; }
.sign-summary-item { display: flex; align-items: baseline; justify-content: center; gap: 4px; padding: 11px 8px; border-right: 1px solid #e3e7eb; }
.sign-summary-item span { color: #778391; font-size: 12px; }
.sign-summary-item strong { color: #2f7d5b; font-size: 23px; }
.sign-summary-item small { color: #8b95a0; }
.sign-summary-item.is-missed strong { color: #b05a53; }
.today-reward { display: flex; align-items: center; justify-content: flex-end; gap: 12px; padding: 10px 16px; color: #596675; }
.today-reward span { color: #8a949f; font-size: 12px; }
.today-reward b { color: #365f7c; font-size: 13px; }
.monthly-calendar { position: relative; display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); overflow: hidden; background: #dfe4e8; border: 1px solid #dfe4e8; border-radius: 6px; gap: 1px; }
.monthly-calendar.is-loading { min-height: 360px; }
.calendar-weekday { height: 34px; color: #6d7986; background: #f3f6f8; font-size: 12px; font-weight: 600; line-height: 34px; text-align: center; }
.calendar-day { position: relative; min-width: 0; height: 94px; padding: 7px 7px 5px; overflow: hidden; background: #fff; }
.calendar-day.is-empty { background: #f8f9fa; }
.calendar-day.is-today { background: #f0f8fc; box-shadow: inset 0 0 0 2px #4b93bd; }
.calendar-day.is-signed { background: #f2faf5; }
.calendar-day.is-missed { background: #faf7f7; }
.calendar-day.is-upcoming { background: #fcfcfd; }
.calendar-day-head { display: flex; align-items: center; justify-content: space-between; gap: 4px; }
.calendar-day-head > span { color: #394858; font-size: 14px; font-weight: 700; }
.calendar-day-head i { color: #949da6; font-size: 10px; font-style: normal; }
.is-signed .calendar-day-head i { color: #3f8b63; }
.is-today .calendar-day-head i { color: #347da7; font-weight: 700; }
.is-missed .calendar-day-head i { color: #a96a65; }
.calendar-reward { min-width: 0; display: flex; align-items: center; gap: 5px; margin-top: 6px; color: #5a6571; }
.calendar-reward span { width: 19px; height: 19px; display: inline-block; flex: 0 0 19px; border-radius: 3px; color: #fff; font-size: 10px; line-height: 19px; text-align: center; }
.calendar-reward b { min-width: 0; overflow: hidden; font-size: 11px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.currency-coin span { background: #3f8f7d; }
.currency-money span { background: #b58a37; }
.calendar-reward.is-xuanjing span { background: #7658a7; }
.calendar-reward.is-xuanjing b { color: #684c91; }
.is-missed .calendar-reward { opacity: .52; }
.signed-mark { position: absolute; right: 5px; bottom: 4px; color: #4a9a6d; font-size: 17px; }
.monthly-sign-panel /deep/ .ivu-alert { margin-top: 12px; }
.monthly-sign-footer { display: flex; justify-content: flex-end; gap: 8px; }
@media (max-width: 760px) {
  .sign-in-modal /deep/ .ivu-modal { top: 8px !important; width: calc(100vw - 12px) !important; margin: 0 6px; }
  .sign-in-modal /deep/ .ivu-modal-body { padding: 12px; }
  .monthly-sign-summary { grid-template-columns: repeat(3, 1fr); }
  .today-reward { grid-column: 1 / -1; justify-content: center; border-top: 1px solid #e3e7eb; }
  .calendar-day { height: 82px; padding: 5px 4px; }
  .calendar-day-head i { display: none; }
  .calendar-reward { gap: 2px; margin-top: 5px; }
  .calendar-reward span { width: 17px; height: 17px; flex-basis: 17px; line-height: 17px; }
  .calendar-reward b { font-size: 9px; }
  .month-switcher { gap: 5px; }
  .month-switcher strong { min-width: 90px; font-size: 16px; }
}
</style>



// WEBPACK FOOTER //
// src/components/SignInModal.vue